import type { BootstrapDTO } from '@clanfire/shared';
import { authApi, bootstrapApi } from '@/api/endpoints';
import { disconnectSocket } from '@/realtime/socket';
import { voiceManager } from '@/realtime/voice';
import { useAuth } from '@/stores/auth';
import { useBlaze } from '@/stores/blaze';
import { useBlocks } from '@/stores/blocks';
import { useMessages } from '@/stores/messages';
import { useThreads } from '@/stores/threads';
import { useConnection, useTyping, useVoice } from '@/stores/realtime';
import { resetGettingStarted, useGettingStarted } from '@/stores/gettingStarted';
import { useSaved } from '@/stores/saved';
import { useServers } from '@/stores/servers';
import { useConversations, useFriends, useNotifications, useReadStates } from '@/stores/social';
import { useUi } from '@/stores/ui';
import { usePresence, useUsers } from '@/stores/users';
import { clearCustomCss } from './appearance';
import { clearDrafts } from './drafts';
import { disablePush } from './pwa';

export let clientConfig: BootstrapDTO['config'] = {
  iceServers: [],
  maxUploadBytes: 10 * 1024 * 1024,
  allowedUploadTypes: [],
  pushPublicKey: null,
  registration: 'open',
  inviteMinRank: 'CEO',
  emailVerification: 'optional',
  gifApiKey: null,
};

/** Loads the post-login snapshot and hydrates every store from it. */
export async function loadBootstrap(): Promise<void> {
  const requestedAt = Date.now();
  applyBootstrap(await bootstrapApi.load(), requestedAt);
}

/** `requestedAt` is when the request was sent; newer live presence events win over the snapshot. */
export function applyBootstrap(data: BootstrapDTO, requestedAt: number) {
  clientConfig = data.config;
  useAuth.getState().setSession(data.user, data.settings);
  useUsers.getState().upsert([data.user]);
  useBlocks.getState().setAll(data.blockedUserIds);
  useServers.getState().hydrate(data.servers);
  useConversations.getState().hydrate(data.conversations);
  useReadStates.getState().hydrate(data.readStates);
  usePresence.getState().mergeSnapshot(data.presences, requestedAt);
  useVoice.getState().hydrate(data.voice);
  useNotifications.getState().setUnreadCount(data.unreadNotificationCount);
  useFriends.getState().setPendingCount(data.pendingFriendRequestCount);
  useSaved.getState().hydrate(data.saved ?? []);
  if (data.gettingStarted) useGettingStarted.getState().hydrate(data.gettingStarted);
}

function resetStores() {
  voiceManager.leave({ silent: true });
  disconnectSocket();
  useServers.getState().reset();
  useMessages.getState().reset();
  useThreads.getState().reset();
  useConversations.getState().reset();
  useReadStates.getState().reset();
  useFriends.getState().reset();
  useNotifications.getState().reset();
  useTyping.getState().reset();
  useVoice.getState().reset();
  useUsers.getState().reset();
  usePresence.getState().reset();
  useBlocks.getState().reset();
  useSaved.getState().reset();
  useBlaze.getState().reset();
  resetGettingStarted();
  useUi.getState().reset();
  useConnection.getState().setStatus('offline');
}

/** Clears all local state without calling the API (session already gone). */
export function endSessionLocally() {
  clearCustomCss();
  resetStores();
  useAuth.getState().clear();
}

export async function logout() {
  await disablePush().catch(() => undefined);
  try {
    await authApi.logout();
  } finally {
    clearDrafts();
    endSessionLocally();
  }
}
